import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { localize } from "./localize";
import { Honeybadger } from "@honeybadger-io/react";


const PERSISTENT_ID = (() => {
  const the_id = String(Date.now() + Math.round(Math.random() * 1000000000000));
  return the_id;
})();

const honeybadger = Honeybadger.configure({
  apiKey: "hbp_E5KGQDUYJ6Q3XetqshwSFKWHDUJB3v3AIvKD",
  environment: window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1" ? "development" : "production",
  revision: "0.1",
});

export const is_local = () => {
  return (
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
  );
};

export const API_URL = is_local() ? "http://localhost:7091" : "";

export type Tool =
  | "snapchat_stories"
  | "instagram_loyalty"
  | "instagram_stories"
  | "tiktok_loyalty"
  | "dating_tracker"
  | "reddit_red_flags"
  | undefined;

export const tool_map = [
  {
    name: "Instagram Loyalty",
    href: "/followers/instagram",
    tool: "instagram_loyalty",
    hero: "See their follows on insta to learn whether they’re loyal, or they’re up to no good.",
    cta: "Reveal Follows",
  },
  {
    name: "TikTok Loyalty",
    href: "/followers/tiktok",
    tool: "tiktok_loyalty",
    hero: "See their follows on tiktok to learn whether they’re loyal, or they’re up to no good.",
    cta: "Reveal Follows",
  },
  {
    name: "Instagram Anonymous Story Viewer",
    href: "/stories/instagram",
    tool: "instagram_stories",
    hero: "Anonymously view Instagram stories without being detected.",
    cta: "Anonymously View Stories",
  },
  {
    name: "Snapchat Anonymous Story Viewer",
    href: "/stories/snapchat",
    tool: "snapchat_stories",
    hero: "Anonymously view Snapchat stories without being detected.",
    cta: "Anonymously View Stories",
  },
  {
    name: "Reddit Red Flags",
    href: "/redflag/reddit",
    tool: "reddit_red_flags",
    hero: "Find red flags in their reddit posting and commenting history.",
    cta: "Search Reddit History",
  },
  /*
    {
      name: "Dating App Tracker",
      href: "/dating/tracker",
      tool: "dating_tracker",
      hero: "Learn whether someone you trust is still using Bumble, or Tinder.",
      cta: "Investigate",
    },
    */
];

export interface DataByUniqueId {
  id: number;
  unique_id: string;
  country_code: string;
  stripe_url: string;
  stripe_url_2: string | null;
  flags: Record<string, any>;
  price_string: string;
  price_string_2: string;
  sent_AddToCart: boolean;
  fbc: string | null;
  fbp: string | null;
  client_ip_address: string | null;
  client_user_agent: string | null;
  stripe_product_id: string;
  stripe_product_id_2: string;
}

export interface User {
  id: number;
  email: string;
  phone_nr?: string;
  subscribed: boolean;
  notifications_to?: string;
  private_investigation_data?: string;
  created_at?: number;
  updated_at?: number;
  onboarding_questions?: Record<string, any>;
  stripe_customer_id: string | undefined;
  billing_url: string;
  unique_id: number;
  breaking_version: string;
}

export interface ProfileAnalysis {
  gender?: string;
  risque_score?: number;
  porn_account?: boolean;
  focus_on_me?: boolean;
  what_its_about?: string;
}

export interface ProfileForAnalysis {
  bio: string;
  username?: string;
  full_name?: string;
  profile_pic_url: string;
  platform_id?: string | number;
  follow_event_on: number;
  analysis?: ProfileAnalysis;
  profile_url?: string;
  platform?: string;
  done_in_depth?: boolean;
}

export interface MediaItem {
  content_url?: string;
  image_array_urls?: string;
  thumbnail_url: string;
  title?: string;
  platform_id: string;
  post_event_on: number;
  mimetype?: string;
  is_video: boolean;
}

export interface RedditItem {
  subreddit: string;
  title: string | null;
  content: string;
  link: string;
}

interface AccountAnalysis {
  id: number;
  not_found: boolean;
  platform: string;
  full_name?: string;
  platform_id?: string;
  profile_pic_url?: string;
  username: string;
  bio?: string;
  is_private: boolean;
  follower_data: {
    followers: ProfileForAnalysis[];
    following: ProfileForAnalysis[];
    reddit_items: RedditItem[];
    total_items?: number;
  };
  media: {
    stories: MediaItem[];
  };
  last_updated_details?: number;
  last_updated_followers?: number;
  last_updated_media?: number;
  created_at?: number;
  updated_at?: number;
}

interface Store {
  call_api: (
    endpoint: string,
    data?: Record<string, any>,
    set_data?: any,
  ) => Promise<any>;
  token?: string;
  set_token: (token: string) => Promise<void>;
  error?: string;
  set_error: (error: string | null) => void;
  unset_error: () => void;
  message?: string;
  set_message: (message: string | null) => void;
  unset_message: () => void;
  loading: boolean;
  start_loading: (ms: number) => void;
  stop_loading: () => void;
  tool: Tool;
  set_tool: (tool: Tool) => void;
  user?: User;
  is_elite: boolean;
  account_analysis?: AccountAnalysis;
  set_account_analysis: (analysis: AccountAnalysis | undefined) => void;
  refresh_analysis: (
    username: string,
    media?: boolean,
    follow?: boolean,
  ) => Promise<void>;
  refresh_current_user: () => Promise<User>;
  refresh_unique_data: () => Promise<any>;
  unique_data: DataByUniqueId;
  login: (email: string) => Promise<void>;
  create_user: (email: string, password: string) => Promise<void>;
  auth_user: (email: string, password: string) => Promise<void>;
  email_token: (email: string) => Promise<void>;
  delete_user: () => Promise<void>;
  logout: () => Promise<void>;
  update_user: (data: { email?: string; password?: string }) => Promise<void>;
  get_load_time: () => number;
  ln: string;
  no_implicit_lang: boolean;
  set_ln: (language: string, explicit_set?: boolean) => void;
  t: (tid: string) => string;
  t_url: (url: string) => string;
  set_tool_color: () => void;
  session_unique_id: string;
  clerk_token: string;
  set_clerk_token: (clerk_token: string) => void;
  activation_call: () => void;
  update_flags: (flags: Record<string, any>) => Promise<void>;
  sub_type: "weekly" | "monthly";
  set_sub_type: (sub_type: "weekly" | "monthly") => void;
  call_add_to_cart: () => Promise<void>;
  call_confirm_user_created: () => Promise<void>;
}

// const CDN_HOST = "https://lululoyal.b-cdn.net";
// const CDN_HOST = "https://lululoyal.com"
const CDN_HOST = is_local() ? "https://lululoyal.com" : "";

export const get_media_url = (url: string | undefined): string => {
  if (!url) return "/favicon.png";
  return `${CDN_HOST}/api/proxy?mediaurl=${encodeURIComponent(url)}`;
};

export const get_cdn_url = (path: string): string => {
  return `${CDN_HOST}${path}`;
};

export const use_store = create<Store>()(
  persist(
    (set, get) => ({
      call_api: async (
        endpoint: string,
        data?: Record<string, any>,
        set_data?: any,
      ) => {
        if (!data) {
          data = {};
        }
        try {
          const headers: Record<string, string> = {
            "Content-Type": "application/json",
          };

          // Add Clerk token to Authorization header if provided
          const clerk_token = get().clerk_token;
          if (clerk_token) {
            headers["Authorization"] = `Bearer ${clerk_token}`;
          }

          const response = await fetch(`${API_URL}/api/${endpoint}`, {
            method: "POST",
            headers,
            body: JSON.stringify(data),
            credentials: "include", // Include cookies
          });
          if (response.ok) {
            const result = await response.json();
            if (set_data) {
              set_data(result);
            }
            return result;
          } else {
            const error_text = await response.text();
            try {
              const error_obj = JSON.parse(error_text);
              get().set_error(error_obj["detail"]);
            } catch (ne) {
              get().set_error(String(error_text));
            }
          }
        } catch (e) {
          get().set_error(String(e));
        }
      },
      token: undefined,
      set_token: async (token: string) => {
        await get().call_api("set_token_cookie", {token: token})

        const msgBuffer = new TextEncoder().encode(token);
        const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray
          .map((b) => b.toString(16).padStart(2, "0"))
          .join("");

        // Just for state handling, don't expose actual token here
        set({ token: hashHex });
      },
      error: undefined,
      set_error: (error: string | null) => {
        if (error) {
          // Report error to Honeybadger
          honeybadger.notify(new Error(error), {
            context: {
              user: get().user?.email,
              tool: get().tool,
              token_exists: !!get().token,
            },
          });
        }
        set({ error: error || undefined });
      },
      unset_error: () => set({ error: undefined }),
      message: undefined,
      set_message: (message: string | null) =>
        set({ message: message || undefined }),
      unset_message: () => set({ message: undefined }),
      loading: false,
      start_loading: (ms: number) => {
        set({ loading: true });
        setTimeout(() => {
          set({ loading: false });
        }, ms);
      },
      stop_loading: () => set({ loading: false }),
      tool: undefined,
      set_tool: (tool: Tool) => {
        set({ tool });
        get().set_tool_color();
        window.scrollTo(0,0);
      },
      user: undefined,
      is_elite: false,
      account_analysis: undefined,
      set_account_analysis: (analysis: AccountAnalysis | undefined) =>
        set({ account_analysis: analysis }),
      refresh_analysis: async (
        username: string,
        media: boolean = false,
        follow: boolean = false,
      ) => {
        if (get().token) {
          await get().refresh_current_user();
          // No need to wait for this, but one spot it can happen
          get().refresh_unique_data();
        }
        const tool = get().tool;
        const result = await get().call_api("analyze", {
          username,
          tool,
          media,
          follow,
        });
        if (result) {
          set({ account_analysis: result });
          get().refresh_unique_data();
        }
      },
      refresh_current_user: async () => {
        const token = get().token;
        const clerk_token = get().clerk_token;
        if (!token && !clerk_token) {
          get().set_error("No authentication token");
          return;
        }

        const result = await get().call_api("get_current_user", {});
        if (result) {
          set({
            user: result,
            is_elite: result.subscribed === true,
          });
          if (result.unique_id && result.unique_id != get().session_unique_id) {
            set({ session_unique_id: result.unique_id });
            document.cookie = `unique_id=${result.unique_id}; path=/; max-age=${60 * 60 * 24 * 365 * 10}; SameSite=Lax`;
          }
        }
        return result;
      },
      refresh_unique_data: async () => {
        document.cookie = `unique_id=${get().session_unique_id}; path=/; max-age=${60 * 60 * 24 * 365 * 10}; SameSite=Lax`;
        const result = await get().call_api("get_unique_data");
        set({ unique_data: result });
      },
      // @ts-ignore
      unique_data: undefined,
      login: async (email: string) => {
        await get().call_api("send_login_email", { email });
      },
      create_user: async (email: string, password: string) => {
        const result = await get().call_api("create_user", { email, password });
        if (result && result.token) {
          await get().set_token(result.token);
          await get().refresh_current_user();
        }
      },
      auth_user: async (email: string, password: string) => {
        const result = await get().call_api("auth_user", { email, password });
        if (result && result.token) {
          await get().set_token(result.token);
          await get().refresh_current_user();
          await get().refresh_unique_data();
        }
      },
      email_token: async (email: string) => {
        await get().call_api("email_token", { email });
      },
      delete_user: async () => {
        await get().call_api("delete_user", {});
        await get().logout();
      },
      logout: async () => {
        // Clear token cookie via API
        await get().call_api("clear_token", {});
        // Clear unique_id cookie
        document.cookie =
          "unique_id=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
        localStorage.clear();
        window.location.reload();
      },
      update_user: async (data: { email?: string; password?: string }) => {
        const result = await get().call_api("update_user", data);
        if (result && result.success) {
          await get().refresh_current_user();
        }
      },
      get_load_time: () => {
        const tool = get().tool;
        const user = get().user;

        if (!user || !user.subscribed) {
          return 11000;
        }

        return String(tool).endsWith("_stories")
          ? 16000
          : tool === "reddit_red_flags"
            ? 15000
            : 85000;
      },
      ln: "en",
      no_implicit_lang: false,
      set_ln: (language: string, explicit_set: boolean = false) => {
        if (explicit_set || !get().no_implicit_lang) {
          if (language !== get().ln) {
            set({ ln: language });
          }
          if (explicit_set && !get().no_implicit_lang) {
            set({ no_implicit_lang: true });
          }
        }
      },
      t: (tid: string) => {
        return localize(tid, get().ln);
      },
      t_url: (url: string) => {
        if (url.startsWith("http") || url.startsWith("www")) {
          return url;
        }
        return `/${get().ln}${url}`;
      },
      set_tool_color: () => {
        const tool = get().tool;
        let color = "rgba(251, 251, 255, 1)"; // default
        let brand1 = "rgba(99, 30, 237, 1)"; // default from index.css
        let brand2 = "rgba(99, 30, 237, 1)"; // default from index.css
        let brand3 = "rgba(99, 30, 237, 1)"; // default from index.css

        if (tool === "instagram_loyalty") {
          color = "rgba(255, 248, 251, 1)";
          brand1 = "rgba(138, 59, 238, 1)";
          brand2 = "rgba(214, 41, 118, 1)";
          brand3 = "rgba(254, 148, 2, 1)";
        } else if (tool === "instagram_stories") {
          color = "rgba(254, 250, 255, 1)";
          brand1 = "rgba(138, 59, 238, 1)";
          brand2 = "rgba(214, 41, 118, 1)";
          brand3 = "rgba(254, 148, 2, 1)";
        } else if (tool === "tiktok_loyalty") {
          color = "rgba(255, 251, 251, 1)";
          brand1 = "rgba(254, 40, 88, 1)";
          brand2 = "rgba(254, 40, 88, 1)";
          brand3 = "rgba(254, 40, 88, 1)";
        } else if (tool === "snapchat_stories") {
          color = "rgba(255, 252, 0, 0.03)";
          brand1 = "rgba(0, 0, 0, 1)";
          brand2 = "rgba(0, 0, 0, 1)";
          brand3 = "rgba(0, 0, 0, 1)";
        } else if (tool === "dating_tracker") {
          color = "rgba(255, 252, 0, 0.03)";
          brand1 = "rgba(255, 97, 94, 1)";
          brand2 = "rgba(247, 79, 115, 1)";
          brand3 = "rgba(240, 75, 117, 1)";
        } else if (tool == "reddit_red_flags") {
          color = "rgba(254, 250, 255, 1)";
          brand1 = "rgba(255, 86, 0, 1)";
          brand2 = "rgba(255, 86, 0, 1)"; //"rgba(255, 67, 0, 1)";
          brand3 = "rgba(255, 86, 0, 1)"; //"rgba(255, 139, 90, 1)";
        }

        document.documentElement.style.setProperty(
          "--color-surface-secondary",
          color,
        );
        document.documentElement.style.setProperty("--color-brand-1", brand1);
        document.documentElement.style.setProperty("--color-brand-2", brand2);
        document.documentElement.style.setProperty("--color-brand-3", brand3);
      },
      session_unique_id: PERSISTENT_ID,
      clerk_token: "",
      set_clerk_token: (clerk_token: string) => {
        set({ clerk_token });
      },
      activation_call: () => {
        get().call_api("give_thanks");
      },
      update_flags: async (flags: Record<string, any>) => {
        await get().call_api("update_flags", flags);
        await get().refresh_unique_data();
      },
      sub_type: "weekly" as "weekly" | "monthly",
      set_sub_type: (sub_type: "weekly" | "monthly") => set({ sub_type }),
      call_add_to_cart: async () => {
        await get().call_api("add_to_cart");
      },
      call_confirm_user_created: async () => {
        await get().call_api("confirm_user_created");
      },
    }),
    {
      name: "loalloyal_storage_v2",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => {
        return Object.fromEntries(
          Object.entries(state).filter(([key]) =>
            [
              "token",
              "ln",
              "no_implicit_lang",
              "account_analysis",
              "session_unique_id",
              "user",
              "unique_data",
              "sub_type",
              "tool",
            ].includes(key),
          ),
        );
      },
    },
  ),
);


